import { addDoc, deleteDoc, doc, getDoc, getDocs, query, updateDoc } from 'firebase/firestore';
import { Dish, dishesRef, generateDishGroupInstanceId } from '@api/Dishes/Dish';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { DISH_FIELDS, logCatalogChange } from '@api/ActivityLogs/CatalogActivity';
import { publishCatalog } from '@api/Catalog/CatalogVersion';
import { PHOTO_UPLOAD_WARNING, isEmbeddedPhoto, resolveDishPhoto } from '@api/Catalog/CatalogPhotos';

export default {
    async saveDish(dish: Dish): Promise<Dish> {
        // Firestore no acepta undefined: normalizamos arrays y defaults antes de escribir
        dish.components = (dish.components || []).map((component) => ({
            recipeId: component.recipeId,
            quantity: Number(component.quantity) || 0
        }));
        // Cada renglón lleva un `instanceId` estable, generado UNA vez (mismo patrón
        // que los ids de opción de los modificadores): es lo que permite adjuntar el
        // MISMO grupo varias veces con etiquetas distintas, y lo que referencian las
        // líneas v2 para poder re-editarse.
        const groupRows = (dish.modifierGroups || []).map((group) => ({
            ...group,
            instanceId: group.instanceId || generateDishGroupInstanceId()
        }));
        // Compat: los productos guardados ANTES de las instancias apuntaban el máximo
        // ligado al groupId del renglón. Al estrenar instanceId hay que reapuntarlo a
        // la instancia (si no, "1 sabor por pieza" dejaría de resolverse). Si el grupo
        // se repite, gana el primer renglón.
        const instanceIdByGroupId = new Map<string, string>();
        groupRows.forEach((group) => {
            if (!instanceIdByGroupId.has(group.groupId)) {
                instanceIdByGroupId.set(group.groupId, group.instanceId);
            }
        });
        dish.modifierGroups = groupRows.map((group) => {
            const linked = group.maxFromGroupId || '';
            const linkedIsInstance = groupRows.some((row) => row.instanceId === linked);
            return {
                groupId: group.groupId,
                instanceId: group.instanceId,
                label: (group.label || '').trim(), // '' = usar el nombre del modificador
                required: group.required ?? false,
                min: Number(group.min) || 0,
                max: Number(group.max) || 0,
                allowQuantity: group.allowQuantity ?? false,
                maxDistinctOptions: Number(group.maxDistinctOptions) || 0, // 0 = sin límite
                maxFromGroupId: linked && !linkedIsInstance ? instanceIdByGroupId.get(linked) || linked : linked // '' = max fijo
            };
        });
        dish.cost = Number(dish.cost) || 0;
        dish.priority = Number(dish.priority) || 1;
        dish.productCategoryId = dish.productCategoryId || '';
        dish.photoSrc = dish.photoSrc || '';
        dish.description = dish.description || '';
        dish.status = dish.status ?? true;
        dish.availableOnline = dish.availableOnline ?? false;
        dish.branchOfficesAvailable = dish.branchOfficesAvailable?.length ? dish.branchOfficesAvailable : ['all'];
        // ⚠️ **La foto NUNCA se escribe embebida en el doc** (D1 del plan del
        // catálogo, 2026-09-09). El formulario entrega un data URI porque el
        // preview lo necesita; aquí se sube a Storage y en el doc queda la URL.
        // Va en el servicio, no en el modal, para que la regla la cumpla
        // cualquier camino que guarde un platillo.
        //
        // Un platillo NUEVO se crea con la foto vacía a propósito: hasta después
        // del `addDoc` no existe el id que da nombre al archivo, y así el data
        // URI no llega a Firestore ni una sola vez.
        const embeddedPhoto = isEmbeddedPhoto(dish.photoSrc) ? dish.photoSrc : '';
        const isNew = !dish.id;
        // El producto ANTES de este guardado, para que la bitácora diga QUÉ cambió
        // (M2). 1 lectura, solo al editar — guardar catálogo ya cuesta 336 al
        // republicar el snapshot, así que no mueve la aguja.
        const before = isNew ? null : await this.getDish(dish.id as string);
        if (isNew) {
            // D9: el documento nace sabiendo cuándo y quién. No cuesta una
            // escritura extra (va en el mismo `addDoc`) y evita tener que
            // registrar un "creó el producto" aparte.
            const data = await addDoc(dishesRef, {
                ...dish,
                ...(await buildCreationStamp()),
                photoSrc: embeddedPhoto ? '' : dish.photoSrc
            });
            dish.id = data.id;
        }
        const photo = await resolveDishPhoto(dish.id as string, dish.photoSrc);
        // ⚠️ **Si Storage falló, el data URI NO entra al documento.** Se guarda el
        // platillo sin esa foto y se avisa.
        //
        // Escribirlo era peor de lo que parecía (encontrado en el re-check del
        // 2026-09-09): `assertSnapshotFits` rechaza publicar un catálogo con una
        // foto embebida, así que ese documento envenenado **bloqueaba la
        // publicación de CUALQUIER cambio del catálogo** —precios, recetas,
        // modificadores— para toda la cadena, hasta que alguien lo limpiara. Y el
        // aviso que debía explicarlo era inalcanzable, porque la publicación
        // lanzaba antes.
        const fotoFallida = isEmbeddedPhoto(photo.photoSrc);
        dish.photoSrc = fotoFallida ? '' : photo.photoSrc;
        await updateDoc(doc(dishesRef, dish.id), { ...dish });
        await publishCatalog('dish');
        await logCatalogChange('dish', dish.id, before, dish, DISH_FIELDS);
        if (embeddedPhoto && fotoFallida) {
            // El platillo YA está guardado: la pantalla reconoce este prefijo,
            // refresca y NO deja que un reintento cree un duplicado.
            throw new Error(
                `${PHOTO_UPLOAD_WARNING} El producto se guardó, pero la foto no se pudo subir. Vuelve a abrirlo y súbela otra vez.`
            );
        }
        return dish;
    },
    async getDishes(): Promise<Dish[]> {
        const dishes: Dish[] = [];
        const queryDoc = query(dishesRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return dishes;
        }
        snapshot.docs.map((doc) => {
            const dish = doc.data() as Dish;
            dish.id = doc.id;
            dishes.push(dish);
        });
        // Mismo orden que el menú viejo: prioridad primero, nombre después
        dishes.sort((a, b) => (a.priority || 0) - (b.priority || 0) || a.name.localeCompare(b.name, 'es'));
        return dishes;
    },
    async getDish(dishId: string): Promise<Dish> {
        const documentData = await getDoc(doc(dishesRef, dishId));
        const dish = documentData.data() as Dish;
        if (dish) {
            dish.id = dishId;
        }
        return dish;
    },
    // Firestore no puede consultar dentro de arrays de objetos: se filtra en cliente
    async getDishesByRecipeId(recipeId: string): Promise<Dish[]> {
        const dishes = await this.getDishes();
        return dishes.filter((dish) => (dish.components || []).some((component) => component.recipeId === recipeId));
    },
    async getDishesByModifierGroupId(modifierGroupId: string): Promise<Dish[]> {
        const dishes = await this.getDishes();
        return dishes.filter((dish) => (dish.modifierGroups || []).some((group) => group.groupId === modifierGroupId));
    },
    async toggleStatus(dishId: string): Promise<void> {
        const dish = await this.getDish(dishId);
        await updateDoc(doc(dishesRef, dishId), { status: !dish.status });
        await publishCatalog('dish-status');
        // Activar o desactivar cambia lo que el cliente ve en el menú.
        await logCatalogChange('dish', dishId, dish, { ...dish, status: !dish.status }, DISH_FIELDS);
        return;
    },
    async deleteDish(dishId: string): Promise<void> {
        await deleteDoc(doc(dishesRef, dishId));
        await publishCatalog('dish-delete');
        // ⚠️ Este renglón queda en el documento de un producto que YA NO EXISTE,
        // así que hoy **nadie puede abrirlo desde la app**: no hay dónde, porque
        // el objeto desapareció de su lista. Se conserva como rastro forense —son
        // 12 en toda la historia— y el límite está anotado en §6 del plan.
        await ActivityLogServ.addActivityLog('dish', dishId, 'eliminó el producto');
        return;
    }
};
